import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Sparkles, ClipboardList, TrendingUp, Bell } from "lucide-react";

const STORAGE_KEY = "healthapp.onboarding_seen";

const SLIDES = [
  {
    icon: ClipboardList,
    title: "Un recorrido en 8 sesiones",
    body: "Cada sesión dura 45-60 min con teoría breve y ejercicios prácticos de TCC. Ve a tu ritmo: puedes pausar y retomar donde lo dejaste.",
    color: "var(--ha-primary)",
  },
  {
    icon: TrendingUp,
    title: "Mide tu progreso",
    body: "En la Sesión 1 tomamos tu línea base (PHQ-9, GAD-7 y más). En la Sesión 8 volvemos a medir para que veas cuánto has avanzado en la pestaña Progreso.",
    color: "var(--ha-accent)",
  },
  {
    icon: Bell,
    title: "Recordatorios y anexos",
    body: "Activa las notificaciones del navegador o el email diario a la hora que elijas. En Anexos tienes frases inspiradoras y 4 técnicas respiratorias guiadas.",
    color: "var(--ha-secondary)",
  },
];

export default function OnboardingModal() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    try {
      const seen = localStorage.getItem(STORAGE_KEY);
      if (!seen) setOpen(true);
    } catch {}
  }, []);

  const close = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch {}
    setOpen(false);
    setStep(0);
  };
  const next = () => (step < SLIDES.length - 1 ? setStep(step + 1) : close());
  const prev = () => setStep(Math.max(0, step - 1));

  const slide = SLIDES[step];
  const Icon = slide.icon;

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
      <DialogContent className="max-w-md" data-testid="onboarding-modal">
        <div className="flex flex-col items-center text-center py-4">
          <div className="h-16 w-16 rounded-full grid place-items-center mb-5 shadow-sm" style={{ backgroundColor: `${slide.color}20` }}>
            <Icon className="w-8 h-8" style={{ color: slide.color }} strokeWidth={1.5} />
          </div>
          <div className="mb-1 flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] font-bold text-[var(--ha-muted)]">
            <Sparkles className="w-3 h-3" strokeWidth={2} /> Paso {step + 1} de {SLIDES.length}
          </div>
          <DialogTitle className="mt-2 text-2xl font-extrabold text-[var(--ha-text)]" data-testid={`onboarding-title-${step}`}>{slide.title}</DialogTitle>
          <DialogDescription className="mt-3 text-[var(--ha-muted)] leading-relaxed text-base px-2">{slide.body}</DialogDescription>

          {/* Dots */}
          <div className="mt-6 flex gap-1.5" data-testid="onboarding-dots">
            {SLIDES.map((_, i) => (
              <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-[var(--ha-primary)]" : "w-1.5 bg-[var(--ha-border)]"}`} />
            ))}
          </div>

          <div className="mt-7 flex items-center justify-between w-full">
            <Button
              data-testid="onboarding-prev"
              variant="ghost"
              onClick={step === 0 ? close : prev}
              className="rounded-full h-10 px-4 text-[var(--ha-muted)]"
            >
              {step === 0 ? "Saltar" : (<><ChevronLeft className="w-4 h-4 mr-1" strokeWidth={2} /> Atrás</>)}
            </Button>
            <Button
              data-testid="onboarding-next"
              onClick={next}
              className="rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-10 px-5"
            >
              {step === SLIDES.length - 1 ? "Empezar" : (<>Siguiente <ChevronRight className="w-4 h-4 ml-1" strokeWidth={2} /></>)}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
